import ReactDOM from 'react-dom/client';
import React, { memo, useEffect, useId, useMemo, useRef, useState } from 'react';
import { numberOrNull } from '../utils';
import { DateInput } from '../utils/DateInput';
import { formatDate, subDays } from 'date-fns';
import { type ChartInstallationModel, getChartData, getChartInstallationModelStructures, getChartSelectionData } from './index';
import { ChartComponent } from './ChartComponent';
import { buildChart } from './buildChart';
import { createStandardQueryClient } from '../utils/react-query/client';
import { ApplicationTypeProvider } from '../../ApplicationTypeContext';
import { QueryClientProvider, useMutation, useQuery } from '@tanstack/react-query';
import { SearchableDropdown } from '../utils/SearchableDropdown/SearchableDropdown';
import { ReactQueryLayout } from '../../components/util/LoadableResourceLayout';
import { convertToDropDownOptionByPredicate, immutableRemoveProperty } from '../../common/util';
import { Alert, Col } from 'react-bootstrap';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    BootstrapCharting: (targetId: string) => void;
  }
}
const emptyObject = Object.freeze({});

function useUrlParams(): URLSearchParams {
  return new URLSearchParams(window.location.search);
}

function useChartingParams() {
  const params = useUrlParams();
  const companyUuid = params.get('cId');
  const siteUuid = params.get('sId');
  const systemUuid = params.get('spId');
  const assetUuid = params.get('aId');
  const dataPointId = numberOrNull(params.get('dpId'));
  return { companyUuid, siteUuid, assetUuid, systemUuid, dataPointId } as const;
}

type DataPointSelection = { installationModelId: number; dataPointId: number };
type DataPointSelectionDictionary<TInstallationModelId extends number = number> = Record<TInstallationModelId, InstallationModelDataPointSelectionDictionary>;
type InstallationModelDataPointSelectionDictionary<TDataPointId extends number = number> = Record<TDataPointId, DataPointSelection>;

export function ChartingContainer(props: {}) {
  const params = useChartingParams();

  const [selectedCompanyUuid, setSelectedCompanyUuid] = useState<string | null>(params.companyUuid ?? null);
  const [selectedSiteUuid, setSelectedSiteUuid] = useState<string | null>(params.siteUuid ?? null);

  const chartSelectionQuery = useQuery({
    queryFn: () => {
      return getChartSelectionData(selectedCompanyUuid, selectedSiteUuid, params.assetUuid, params.systemUuid);
    },
    queryKey: ['chartSelectionData', selectedCompanyUuid, selectedSiteUuid, params.assetUuid, params.systemUuid]
  });
  const chartSelection = chartSelectionQuery.data;

  const chartingInstallationModelStructureQuery = useQuery({
    queryFn: () => getChartInstallationModelStructures(selectedSiteUuid!),
    queryKey: ['chartingInstallationModelStructure', selectedSiteUuid],
    enabled: !!selectedSiteUuid,
    refetchOnWindowFocus: false
  });
  const dataPointsDictionary = chartingInstallationModelStructureQuery.data?.dataPoints ?? {};

  const companyOptions = useMemo(
    () =>
      convertToDropDownOptionByPredicate(
        chartSelectionQuery.data?.companies ?? [],
        (c) => c.name,
        (c) => c.uuid
      ),
    [chartSelectionQuery.data]
  );
  const selectedCompanyOption = useMemo(() => companyOptions.find((c) => c.value === selectedCompanyUuid), [companyOptions, selectedCompanyUuid]);
  // if only one option, go ahead and select it
  useEffect(() => {
    if (companyOptions.length === 1) {
      setSelectedCompanyUuid(companyOptions[0].value);
    }
  }, [companyOptions]);
  const siteOptions = useMemo(
    () =>
      convertToDropDownOptionByPredicate(
        chartSelectionQuery.data?.sites ?? [],
        (s) => s.name,
        (s) => s.uuid
      ),
    [chartSelectionQuery.data]
  );
  const selectedSiteOption = useMemo(() => siteOptions.find((s) => s.value === selectedSiteUuid), [siteOptions, selectedSiteUuid]);
  // if only one option, go ahead and select it
  useEffect(() => {
    if (siteOptions.length === 1) {
      setSelectedSiteUuid(siteOptions[0].value);
    }
  }, [siteOptions]);
  const chartId = useId();
  const systemProcesses = useMemo(
    () => chartingInstallationModelStructureQuery.data?.systemProcesses ?? [],
    [chartingInstallationModelStructureQuery.data?.systemProcesses]
  );
  const assets = useMemo(() => chartingInstallationModelStructureQuery.data?.assets ?? [], [chartingInstallationModelStructureQuery.data?.assets]);
  const [selectedAssetDataPoints, setSelectedAssetDataPoints] = useState<DataPointSelectionDictionary>({});
  const [selectedSystemProcessDataPoints, setSelectedSystemProcessDataPoints] = useState<DataPointSelectionDictionary>({});
  const [selectedSystemProcesses, setSelectedSystemProcesses] = useState<number[]>([]);
  const [selectedAssets, setSelectedAssets] = useState<number[]>([]);

  const systemSelectionInitialized = useRef(false);
  const assetSelectionInitialized = useRef(false);
  const shouldAutomaticallyGenerateChart = useRef(!!params.dataPointId);

  const [startDate, setStartDate] = useState<Date | null>(subDays(new Date(), 7));
  const [endDate, setEndDate] = useState<Date | null>(new Date());

  const {
    mutateAsync: generateChart,
    isPending: isGeneratingChart,
    isError: generatingChartError,
    data: generatedChart,
    reset: resetGenerateChart
  } = useMutation({
    mutationFn: async () => {
      const oData = await getChartData(
        Object.values(selectedSystemProcessDataPoints).flatMap((v) => Object.values(v)),
        Object.values(selectedAssetDataPoints).flatMap((v) => Object.values(v)),
        formatDate(startDate!, 'yyyy/MM/dd'),
        formatDate(endDate!, 'yyy/MM/dd')
      );
      if (oData.hasdata) await buildChart(chartId, oData.data!);
      return oData;
    },
    networkMode: 'always'
  });
  // if the page is navigated to with a data point id then set the selections.
  useEffect(() => {
    if (chartSelection?.systemProcessId && systemProcesses.length > 0 && !systemSelectionInitialized.current) {
      setSelectedSystemProcesses([chartSelection.systemProcessId]);
      setSelectedSystemProcessDataPoints({
        [chartSelection.systemProcessId]: { [params.dataPointId!]: { installationModelId: chartSelection.systemProcessId, dataPointId: params.dataPointId! } }
      });
      systemSelectionInitialized.current = true;
    }
  }, [chartSelection?.systemProcessId, params.dataPointId, systemProcesses.length]);

  // if the page is navigated to with an assetId, then set the selection
  useEffect(() => {
    if (chartSelection?.assetId && assets.length > 0 && !assetSelectionInitialized.current) {
      setSelectedAssets([chartSelection.assetId]);
      assetSelectionInitialized.current = true;
    }
  }, [chartSelection?.assetId, params.assetUuid, assets.length]);

  // generate the chart when the page is navigated to with a data point id and all the selections are ready
  useEffect(() => {
    if (
      (Object.keys(selectedAssetDataPoints).length > 0 || Object.keys(selectedSystemProcessDataPoints).length > 0) &&
      shouldAutomaticallyGenerateChart.current
    ) {
      void generateChart();
      shouldAutomaticallyGenerateChart.current = false;
    }
  }, [generateChart, selectedAssetDataPoints, selectedSystemProcessDataPoints]);

  function resetValues() {
    setSelectedCompanyUuid(null);
    setSelectedSiteUuid(null);
    setSelectedSystemProcesses([]);
    setSelectedAssets([]);
    setSelectedAssetDataPoints({});
    setSelectedSystemProcessDataPoints({});
    setStartDate(subDays(new Date(), 7));
    setEndDate(new Date());
    document.getElementById(chartId)!.innerHTML = '';
    resetGenerateChart();
  }

  return (
    <>
      {generatedChart && !generatedChart.hasdata && generatedChart.message && <h4 className='text-center mb-4 mt-2'>{generatedChart.message}</h4>}
      <div className='row'>
        <div className='structure-selection col-lg-3'>
          <div className='panel panel-inverse' data-sortable-id='form-plugins-1' style={{ maxHeight: '100%', minHeight: '100%' }}>
            <div className='panel-heading'>
              <h4 className='panel-title'>Primary Selections</h4>
            </div>
            <div className='panel-body'>
              <ReactQueryLayout state={chartSelectionQuery}>
                <form className='form-horizontal form-bordered'>
                  <div className='form-group row'>
                    <label className='col-md-4 text-md-right col-form-label'>Company</label>
                    <Col md={8}>
                      <SearchableDropdown
                        disabled={!!params.companyUuid}
                        dropdownData={companyOptions}
                        value={selectedCompanyOption}
                        onSelect={setSelectedCompanyUuid}
                      />
                    </Col>
                  </div>
                  <div className='form-group row'>
                    <label className='col-md-4 text-md-right col-form-label'>Sites</label>
                    <Col md={8}>
                      <SearchableDropdown disabled={!!params.siteUuid} dropdownData={siteOptions} value={selectedSiteOption} onSelect={setSelectedSiteUuid} />
                    </Col>
                  </div>
                  <ReactQueryLayout state={chartingInstallationModelStructureQuery}>
                    <>
                      <div className='form-group row'>
                        <label className='col-md-4 text-md-right col-form-label'>System Processes</label>
                        <div className='col-md-8'>
                          {systemProcesses.map((sp) => (
                            <div key={sp.id} className='form-check' style={{ paddingLeft: '25px', paddingRight: '25px' }}>
                              <input
                                id={`sp_${sp.id}`}
                                name={`sp_${sp.id}`}
                                type='checkbox'
                                checked={selectedSystemProcesses.includes(sp.id)}
                                onChange={(e) => setSelectedSystemProcesses((c) => (e.target.checked ? [...c, sp.id] : c.filter((id) => id !== sp.id)))}
                                className={'form-check-input'}
                              />
                              <label className='form-check-label' htmlFor={`sp_${sp.id}`}>
                                {sp.name}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className='form-group row'>
                        <label className='col-md-4 text-md-right col-form-label'>Assets</label>
                        <div className='col-md-8'>
                          {assets
                            .filter((a) => !a.isRmxHub)
                            .map((asset) => (
                              <AssetCheckBox key={asset.id} asset={asset} selectedAssets={selectedAssets} setSelectedAssets={setSelectedAssets} />
                            ))}
                        </div>
                      </div>
                      {assets.filter((a) => a.isRmxHub).length > 0 && (
                        <>
                          <div className='form-group row'>
                            <label className='col-md-4 text-md-right col-form-label'>Hubs and Gateways</label>
                            <div className='col-md-8'>
                              {assets
                                .filter((a) => a.isRmxHub)
                                .map((asset) => (
                                  <AssetCheckBox key={asset.id} asset={asset} selectedAssets={selectedAssets} setSelectedAssets={setSelectedAssets} />
                                ))}
                            </div>
                          </div>
                        </>
                      )}
                      <div className='form-group row'>
                        <label className='col-md-4 text-md-right col-form-label'>Choose Date Range</label>
                        <div className='col-md-8'>
                          <label style={{ display: 'block' }}>Start</label>
                          <DateInput value={startDate} onChange={setStartDate} />
                          <br />
                          <br />
                          <label style={{ display: 'block' }}>End</label>
                          <DateInput value={endDate} onChange={setEndDate} />
                        </div>
                      </div>
                    </>
                  </ReactQueryLayout>
                  <div className='row' style={{ float: 'right', padding: '10px' }}>
                    <div className='col-md-12'>
                      <button onClick={resetValues} style={{ backgroundColor: '#00CDE5' }} className='btn btn-block btn-lg'>
                        Reset Values
                      </button>
                    </div>
                  </div>
                </form>
              </ReactQueryLayout>
            </div>
          </div>
        </div>
        <div className='datapoint-selection col-lg-2'>
          <div className='panel panel-inverse' data-sortable-id='form-plugins-2' style={{ maxHeight: '100%', minHeight: '100%' }}>
            <div className='panel-heading ui-sortable-handle'>
              <h4 className='panel-title'>Datapoint Parameters are based on selections</h4>
            </div>
            <div className='panel-body bg-white-transparent-1'>
              <div id='accordion' className='card-accordion'>
                <div className='row'>
                  <div className='col-lg-12'>
                    {selectedSystemProcesses.map((spId) => {
                      const systemProcess = systemProcesses.find((s) => s.id === spId)!;
                      return (
                        <DataPointSelectionCard
                          selectedDataPoints={selectedSystemProcessDataPoints[systemProcess.id] ?? emptyObject}
                          key={spId}
                          installationModel={systemProcess}
                          dataPointsDictionary={dataPointsDictionary}
                          setSelectedDataPoints={setSelectedSystemProcessDataPoints}
                        />
                      );
                    })}
                  </div>
                </div>
              </div>

              <div id='accordion' className='card-accordion'>
                <div className='row'>
                  <div className='col-lg-12'>
                    {selectedAssets.map((aId) => {
                      const asset = assets.find((s) => s.id === aId)!;
                      return (
                        <DataPointSelectionCard
                          selectedDataPoints={selectedAssetDataPoints[asset.id] ?? emptyObject}
                          key={aId}
                          installationModel={asset}
                          dataPointsDictionary={dataPointsDictionary}
                          setSelectedDataPoints={setSelectedAssetDataPoints}
                        />
                      );
                    })}
                  </div>
                </div>
              </div>

              <div id='asset_datapoints' />
            </div>
          </div>
        </div>
        <div className='graph-section col-lg-7'>
          <div className='panel panel-inverse' data-sortable-id='form-plugins-2' style={{ maxHeight: '100%', minHeight: '100%' }}>
            <div className='panel-heading ui-sortable-handle'>
              {generatingChartError && <Alert variant={'danger'}>Failed to get graph data</Alert>}
              {isGeneratingChart && <FakeLoader enabled={true} />}
              <h4 className='panel-title'>Graph Results (Click and drag in the plot area to zoom in OR Pinch the graph to zoom in on mobile)</h4>
            </div>
            <div className='panel-body bg-white-transparent-1' style={{ maxHeight: '450px', minHeight: '450px' }}>
              <figure className='highcharts-figure' style={{ width: '100%' }}>
                <ChartComponent id={chartId} />
              </figure>
              <div id='chart_results' />
              {/* end content area */}
            </div>
            <div className='row' style={{ float: 'left', padding: '10px' }}>
              <div className='col-md-12'>
                <form className='form-horizontal form-bordered'>
                  <div className='form-group row'>
                    <div className='col-md-12'>
                      <button type='button' onClick={() => generateChart()} style={{ backgroundColor: '#FF7C00' }} className='btn btn-block btn-lg'>
                        Generate Graph
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function AssetCheckBox({
  asset,
  setSelectedAssets,
  selectedAssets
}: {
  asset: ChartInstallationModel;
  selectedAssets: number[];
  setSelectedAssets: React.Dispatch<React.SetStateAction<number[]>>;
}) {
  return (
    <div className='form-check' style={{ paddingLeft: '25px', paddingRight: '25px' }}>
      <input
        id={`asset_${asset.id}`}
        name={`asset_${asset.id}`}
        type='checkbox'
        checked={selectedAssets.includes(asset.id)}
        className='form-check-input asset_checkbox'
        onChange={(e) => setSelectedAssets((c) => (e.target.checked ? [...c, asset.id] : c.filter((id) => id !== asset.id)))}
      />
      <label className='form-check-label' htmlFor={`asset_${asset.id}`}>
        {asset.name}
      </label>
    </div>
  );
}

function FakeLoader({ enabled }: { enabled: boolean }) {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    const interval = setInterval(() => {
      setProgress((p) => Math.min(p + (100 - p) / 200, 100));
    }, 100);
    return () => clearInterval(interval);
  }, [enabled]);
  const label = useMemo(() => (progress >= 50 ? 'Preparing Data' : progress >= 25 && progress < 50 ? 'Analyzing Data' : 'Fetching Data'), [progress]);
  const roundedProgress = Math.round(progress);
  return (
    <div className='row'>
      <div className='col-6'>
        <div id='Progress_Status'>
          <div id='myprogressBar' style={{ width: roundedProgress + '%' }}>
            {roundedProgress}%
          </div>
        </div>
      </div>
      <div className='col-4' style={{ float: 'left' }}>
        <h3 id='progressbar_status' style={{ color: 'white' }}>
          {label}
        </h3>
      </div>
    </div>
  );
}

const DataPointSelectionCard = memo(function DataPointSelectionCard({
  installationModel,
  dataPointsDictionary,
  setSelectedDataPoints,
  selectedDataPoints
}: {
  installationModel: ChartInstallationModel;
  dataPointsDictionary: Record<number, { id: number; name: string }>;
  setSelectedDataPoints: (v: DataPointSelectionDictionary | ((v: DataPointSelectionDictionary) => DataPointSelectionDictionary)) => void;
  selectedDataPoints: InstallationModelDataPointSelectionDictionary;
}) {
  const dataPoints = useMemo(
    () => installationModel.dataPoints.map((id) => dataPointsDictionary[id]).sort((a, b) => a.name.localeCompare(b.name)),
    [dataPointsDictionary, installationModel]
  );
  const dropDownId = 'card_' + useId().replaceAll(':', '_');
  return (
    <>
      <div className='card'>
        <div className='card-header bg-black text-white pointer-cursor' data-toggle='collapse' data-target={'#' + dropDownId} aria-expanded='true'>
          {installationModel.name} Data Points <i className='fas fa-lg fa-fw m-r-0 fa-arrow-alt-circle-down' style={{ float: 'right' }} />
        </div>
        <div id={dropDownId} className='collapse show' data-parent='#accordion' style={{}}>
          <div className='card-body'>
            <div className='row' style={{ overflowX: 'hidden', maxHeight: '225px', minHeight: '225px' }}>
              {dataPoints.map((dp) => (
                <DataPointsSelection
                  isChecked={!!selectedDataPoints[dp.id]}
                  key={dp.id}
                  dp={dp}
                  installationModelId={installationModel.id}
                  setSelectedDataPoints={setSelectedDataPoints}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
});

const DataPointsSelection = memo(function DataPointsSelection({
  dp,
  setSelectedDataPoints,
  installationModelId,
  isChecked
}: {
  dp: { id: number; name: string };
  installationModelId: number;
  setSelectedDataPoints: (v: DataPointSelectionDictionary | ((v: DataPointSelectionDictionary) => DataPointSelectionDictionary)) => void;
  isChecked: boolean;
}) {
  const checkBoxId = useId().replaceAll(':', '_');
  return (
    <div key={dp.id} className='form-check' style={{ paddingLeft: '25px', paddingRight: '25px' }}>
      <input
        id={checkBoxId}
        name={checkBoxId}
        type='checkbox'
        className='form-check-input'
        checked={isChecked}
        onChange={(e) => {
          setSelectedDataPoints((c) =>
            e.target.checked
              ? { ...c, [installationModelId]: { ...c[installationModelId], [dp.id]: { installationModelId, dataPointId: dp.id } } }
              : { ...c, [installationModelId]: immutableRemoveProperty(c[installationModelId], dp.id) }
          );
        }}
      />
      <label className='form-check-label' htmlFor={checkBoxId}>
        {dp.name}
      </label>
    </div>
  );
});

const queryClient = createStandardQueryClient();
window.BootstrapCharting = (targetId: string) => {
  const root = ReactDOM.createRoot(document.getElementById(targetId) as HTMLElement);
  root.render(
    <>
      <React.StrictMode>
        <ApplicationTypeProvider value='Portal'>
          <QueryClientProvider client={queryClient}>
            <ChartingContainer />
          </QueryClientProvider>
        </ApplicationTypeProvider>
      </React.StrictMode>
    </>
  );
};
